import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import type { AppDispatch } from "_shared/_stores/store-types";
import { markMemberSigningAuthority } from "_shared/entity-members/_api/entity-member-api";
import EntityMemberSummary from "_shared/entity-members/entity-member-summary";
import Guid from "_shared/guids/guid";
import { useSnackbar } from "_shared/_providers/snackbars/snackbars";
import { offerUndo } from "_shared/_providers/snackbars/undo-snackbar";
import PermissionFilterParameters from "permissions/_api/queries/filtering/permission-filter-parameters";
import PermissionQuery, { PermissionView } from "permissions/_api/queries/permission-query";
import {
  useAddPermissionMutation,
  useDisablePermissionMutation,
  useEnablePermissionMutation,
  useLazyGetPermissionsQuery,
  useUpdatePermissionMutation,
} from "permissions/_api/permissions-api-service";
import type { PermissionJSON } from "permissions/_schemas/permission-schema";
import Permission from "permissions/permission";
import { LegalEntityAccess } from "permissions/resources/legal-entities/legal-entities-permissions-resources";
import { LegalEntityAccessType } from "permissions/resources/legal-entities/legal-entity-access-types";
import { AccessLevel, ResourceType, Scope } from "permissions/resources/permission-resource-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

/** How long the Undo stays offered while nobody is pointing at it or focused in it. */
export const SIGNING_AUTHORITY_UNDO_MS = 8000;

/** A grant or a removal, from a row's menu: the member, and the menu button focus goes back to. */
export type MemberSigningAuthorityAction = (member: EntityMemberSummary, menuButton: HTMLElement) => void;

export type MemberSigningAuthority = {
  grant: MemberSigningAuthorityAction;
  remove: MemberSigningAuthorityAction;
  /** The members, by user id, whose grant or removal has not been answered yet. */
  pendingUserIds: ReadonlySet<string>;
};

/**
 * Signing authority as the server reads it: `LegalEntityAccess`, access type Signing Authority, at
 * Read/Write. A Read-Only grant of the type signs nothing, so it is neither shown as a signer nor
 * removed here.
 */
function isSigningAuthorityGrant(permission: Permission): boolean {
  return (
    isSigningAuthorityType(permission) &&
    (permission.resource as LegalEntityAccess).accessLevel === AccessLevel["Read/Write"]
  );
}

/** An enabled grant of the Signing Authority type, at any level. */
function isSigningAuthorityType(permission: Permission): boolean {
  const resource = permission.resource;
  return (
    !permission.isDisabled &&
    resource instanceof LegalEntityAccess &&
    resource.accessType === LegalEntityAccessType["Signing Authority"]
  );
}

/** The member's enabled grants of the Signing Authority type, from a fresh read. */
function heldSigningGrants(member: EntityMemberSummary, page: { data: PermissionJSON[] }): Permission[] {
  return Permission.fromJSONArray(page.data).filter(
    (permission) => permission.granteeUserId.isEqualTo(member.userId) && isSigningAuthorityType(permission),
  );
}

/**
 * The grant the person page writes for signing authority: Read/Write, Entity-Wide. Every assertion
 * the server makes of signing authority asks for Read/Write. A member who already holds a Read-Only
 * grant of the type has it raised rather than duplicated, because the server answers a grant it can
 * widen by widening it.
 */
function signingAuthorityGrantFor(member: EntityMemberSummary, grantorUserId: Guid): Permission {
  return new Permission({
    resource: LegalEntityAccess.create({
      resourceType: ResourceType.LegalEntityAccess,
      accessType: LegalEntityAccessType["Signing Authority"],
      accessLevel: AccessLevel["Read/Write"],
      scope: Scope["Entity-Wide"],
    }),
    granteeUserId: member.userId,
    grantorUserId,
    entityId: member.legalEntityId,
    version: 0,
  });
}

/** The signing-authority grants a member holds now, read fresh so each carries its current version. */
function signingAuthorityQueryFor(member: EntityMemberSummary): PermissionQuery {
  return new PermissionQuery({
    permissionView: PermissionView.Flat,
    paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: 100 }),
    filterParameters: new PermissionFilterParameters({
      granteeUserIds: [member.userId],
      resourceTypes: [ResourceType.LegalEntityAccess],
      showDisabled: false,
    }),
  });
}

type DisablePermission = ReturnType<typeof useDisablePermissionMutation>[0];
type EnablePermission = ReturnType<typeof useEnablePermissionMutation>[0];
type UpdatePermission = ReturnType<typeof useUpdatePermissionMutation>[0];

/** Disables each grant in turn, and hands back the rows the server answered with. */
async function disableAll(disable: DisablePermission, grants: readonly Permission[]): Promise<PermissionJSON[]> {
  const disabled: PermissionJSON[] = [];
  for (const grant of grants) disabled.push(await disable({ permission: grant }).unwrap());
  return disabled;
}

/**
 * Re-grants each removed grant from the row its removal answered with, so the version sent is the
 * one the removal left.
 */
async function regrantAll(enable: EnablePermission, removed: readonly PermissionJSON[]): Promise<void> {
  for (const permission of removed) await enable({ permission: Permission.fromJSON(permission) }).unwrap();
}

/**
 * Puts a grant the server raised back to the level it had, at the version the raise left. The row
 * belonged to the member before this grant, so undoing the grant must not disable it.
 */
async function lowerAgain(update: UpdatePermission, granted: PermissionJSON, before: Permission): Promise<void> {
  const raised = Permission.fromJSON(granted);
  await update({
    permission: new Permission({
      id: raised.id,
      resource: before.resource,
      granteeUserId: raised.granteeUserId,
      grantorUserId: raised.grantorUserId,
      entityId: raised.entityId,
      note: raised.note,
      version: raised.version,
    }),
  }).unwrap();
}

/** Focus goes back to the row's menu, if the row is still drawn. */
function returnFocusTo(menuButton: HTMLElement) {
  if (menuButton.isConnected) menuButton.focus();
}

/**
 * Grants or removes a member's signing authority from the roster, and offers to undo it.
 *
 * Both read the member's grants first. Grant posts the one grant the person page writes, unless the
 * member already holds it. Remove disables each signing-authority grant the member holds — normally
 * one — with `DELETE /permissions/{id}`. Either way the Role cell is redrawn from the answer, without
 * a refetch, and a snackbar names the person and offers Undo: a grant is undone by removing the grant
 * just made (or lowering a raised one again), a removal by re-granting what was removed. A re-grant mints
 * a new permission whose `supersededPermissionId` is the one removed, so nothing here keeps a removed
 * id for later: the next removal reads the member's grants afresh.
 */
export default function useMemberSigningAuthority(sessionUserId: Guid | undefined): MemberSigningAuthority {
  const dispatch = useDispatch<AppDispatch>();
  const { enqueueSnackbar } = useSnackbar();
  const [addPermission] = useAddPermissionMutation();
  const [disablePermission] = useDisablePermissionMutation();
  const [enablePermission] = useEnablePermissionMutation();
  const [updatePermission] = useUpdatePermissionMutation();
  const [readPermissions] = useLazyGetPermissionsQuery();
  const [pendingUserIds, setPendingUserIds] = useState<ReadonlySet<string>>(new Set());

  // The button each in-flight change came from. Holding a change disables that button, and a disabled
  // button drops focus to the page, so focus is handed back once the change settles and the button is
  // enabled again — but only if nothing else has taken focus meanwhile.
  const focusOwed = useRef(new Map<string, HTMLElement>());
  useEffect(() => {
    for (const [userId, button] of focusOwed.current) {
      if (pendingUserIds.has(userId)) continue;
      focusOwed.current.delete(userId);
      const active = document.activeElement;
      if ((active === null || active === document.body) && button.isConnected) button.focus();
    }
  }, [pendingUserIds]);

  const setPending = useCallback((userId: Guid, pending: boolean, focusReturnsTo?: HTMLElement) => {
    if (focusReturnsTo) focusOwed.current.set(userId.value, focusReturnsTo);
    setPendingUserIds((current) => {
      const next = new Set(current);
      if (pending) next.add(userId.value);
      else next.delete(userId.value);
      return next;
    });
  }, []);

  const reportFailure = useCallback(
    (error: unknown, fallback: string) => {
      enqueueSnackbar(getErrorMessageFromUnknown(error) ?? fallback, { variant: "error" });
    },
    [enqueueSnackbar],
  );

  /** A success snackbar that names the change and offers to undo it until it is dismissed. */
  const offerUndoOf = useCallback(
    (message: string, menuButton: HTMLElement, undo: () => Promise<void>) => {
      offerUndo({
        message,
        durationMs: SIGNING_AUTHORITY_UNDO_MS,
        returnFocus: () => returnFocusTo(menuButton),
        undo: () => void undo(),
      });
    },
    [],
  );

  const grant = useCallback<MemberSigningAuthorityAction>(
    (member, menuButton) => {
      if (!sessionUserId) return;
      setPending(member.userId, true, menuButton);
      // Read first. The server answers a grant somebody already holds with that grant, so the answer
      // alone cannot tell a new grant from another administrator's, and Undo must only take back what
      // this press created.
      void readPermissions({ query: signingAuthorityQueryFor(member) })
        .unwrap()
        .then(async (page) => {
          const held = heldSigningGrants(member, page);
          if (held.some(isSigningAuthorityGrant)) {
            dispatch(markMemberSigningAuthority(member.userId, true));
            enqueueSnackbar(`${member.fullName} already holds signing authority`, { variant: "info" });
            return;
          }
          const granted = await addPermission({ permission: signingAuthorityGrantFor(member, sessionUserId) }).unwrap();
          dispatch(markMemberSigningAuthority(member.userId, true));
          // A Read-Only grant of the type is raised in place rather than duplicated. Undo then lowers
          // it again instead of disabling a row the member held before.
          const raised = held.find((permission) => permission.id?.value === granted.id);
          // Chosen outside the `try` below: a conditional inside one makes the compiler skip the hook.
          const takeBack = raised
            ? () => lowerAgain(updatePermission, granted, raised)
            : () => disableAll(disablePermission, [Permission.fromJSON(granted)]);
          offerUndoOf(`Granted signing authority to ${member.fullName}`, menuButton, async () => {
            setPending(member.userId, true, menuButton);
            try {
              await takeBack();
              dispatch(markMemberSigningAuthority(member.userId, false));
            } catch (error) {
              reportFailure(error, `Could not undo signing authority for ${member.fullName}.`);
            }
            setPending(member.userId, false);
          });
        })
        .catch((error: unknown) => reportFailure(error, `Could not grant signing authority to ${member.fullName}.`))
        .then(() => setPending(member.userId, false));
    },
    [
      addPermission,
      disablePermission,
      dispatch,
      enqueueSnackbar,
      offerUndoOf,
      readPermissions,
      reportFailure,
      sessionUserId,
      setPending,
      updatePermission,
    ],
  );

  const remove = useCallback<MemberSigningAuthorityAction>(
    (member, menuButton) => {
      setPending(member.userId, true, menuButton);
      void readPermissions({ query: signingAuthorityQueryFor(member) })
        .unwrap()
        .then(async (page) => {
          const grants = heldSigningGrants(member, page).filter(isSigningAuthorityGrant);
          if (grants.length === 0) {
            // The roster was behind: somebody else removed it first. Nothing was removed here, so
            // there is nothing to undo.
            dispatch(markMemberSigningAuthority(member.userId, false));
            enqueueSnackbar(`${member.fullName} no longer holds signing authority`, { variant: "info" });
            return;
          }
          const removed = await disableAll(disablePermission, grants);
          dispatch(markMemberSigningAuthority(member.userId, false));
          offerUndoOf(`Removed signing authority from ${member.fullName}`, menuButton, async () => {
            setPending(member.userId, true, menuButton);
            try {
              await regrantAll(enablePermission, removed);
              dispatch(markMemberSigningAuthority(member.userId, true));
            } catch (error) {
              reportFailure(error, `Could not restore signing authority to ${member.fullName}.`);
            }
            setPending(member.userId, false);
          });
        })
        .catch((error: unknown) => reportFailure(error, `Could not remove signing authority from ${member.fullName}.`))
        .then(() => setPending(member.userId, false));
    },
    [disablePermission, dispatch, enablePermission, enqueueSnackbar, offerUndoOf, readPermissions, reportFailure, setPending],
  );

  // Stable on purpose: the roster's column definitions depend on it, and a fresh object on every
  // render would rebuild them every render whether or not the compiler memoizes this hook.
  return useMemo(() => ({ grant, remove, pendingUserIds }), [grant, remove, pendingUserIds]);
}
